import { defineComponent as _defineComponent } from 'vue'

// Element's dropdown parts aren't globally registered (the custom fields Copy menu uses them).
import { Dropdown, DropdownMenu, DropdownItem } from 'element-ui'

const __default__ = {
  name: 'waiver-drawer',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem
  }
}

import { ref, reactive, computed, watch, nextTick, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import moment from 'moment'
import { Auth } from 'aws-amplify'
import Badge from '@/components/Badge'
import { API_LOCATION } from '@/api-config'
import { fetchWaiverDetail } from '@/util/waiverSignature'
import { displayTime, formattedDate } from '@/plugins/dateFormatter'
import { formatGroupDate, formatGroupTime, isValidDate } from '@/plugins/dates'
import { formatPhoneDisplay } from '@/util/phoneFormatter'

// No `| null` in these types: Vue 2.7 compiles it to `type: [..., null]`, which Vue 2 rejects
// ("null is not a constructor"). Optional props already accept null from the parent.
type ChangeAction = 'moved' | 'assigned' | 'copied' | 'cleared' | 'participation'

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    show: { type: Boolean },
    waiver: null,
    currentCustomerId: null,
    readOnly: { type: Boolean, default: false }
  },
  emits: ["close", "changed", "deleted"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void
  (e: 'changed', payload: { action: ChangeAction; waiverId: number; notParticipating?: boolean }): void
  (e: 'deleted', waiverId: number): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { show: boolean, waiver?: Record<string, any>, currentCustomerId?: number | string, readOnly: boolean }

// Waiver detail drawer shared by Waivers > History and the customer page Waivers tab.
// The parent owns `show` and passes the clicked list row as `waiver`; the drawer shows
// it at once, then merges in the full detail (signature, custom fields, consent).
// Move, Copy, Clear booking and Delete report back through `changed` / `deleted` so
// each page refreshes its own list.




const { proxy } = getCurrentInstance() as any
const group = JSON.parse(localStorage.getItem('group') || '{}')
const dateFormat = group.date_format
const timeFormat = group.time_format

const selectedWaiver = ref<any>(null)
const selectedBooking = ref<any>('')
const bookings = ref<any[]>([])
const emptyBooking = ref(false)
const current = ref('')
const customFieldResponses = ref<any[]>([])
const consentFields = ref<any[]>([])
const pdfGenerating = ref(false)
const drawerLoading = ref(false)
const drawerView = ref<'detail' | 'booking-selector'>('detail')
const isCopyMode = ref(false)
const bookingsLoading = ref(false)
const drawerShowAllBookings = ref(false)
const saving = ref(false)
const clearPrompt = reactive({ show: false, loading: false })
const deletePrompt = reactive({ show: false, loading: false })
const waiverPdf = ref<any>(null)

function hasPermission(scope: string): boolean {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  return permissions.some((p: string) => p === scope)
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function groupUrl(): string {
  return API_LOCATION + 'companies/' + localStorage.getItem('companyId') + '/groups/' + group.id
}

function waiverUrl(): string {
  return groupUrl() + '/waivers/' + selectedWaiver.value.id
}

// A walk-in has no event and no booking slot.
const isWalkinWaiver = computed(() => {
  const w = selectedWaiver.value
  return !!w && !w.booking_slot_id && !w.game_id
})

// Mirrors the lambda's clear gates so the button only shows when the clear will be accepted:
// on a booking that is not completed, signed on a waiver template in this group.
const canClearBooking = computed(() => {
  const w = selectedWaiver.value
  if (!w || !w.booking_slot_id) return false
  if (w.booking_status === 'completed') return false
  return !!w.waiver_configuration_id && Number(w.template_group_id) === Number(group.id)
})

// Bookings filtered to only "booked" or "completed" by default. When the user
// expands ("Show N other slots"), all bookings are shown.
const filteredBookings = computed(() => {
  if (!bookings.value || bookings.value.length === 0) return []
  if (drawerShowAllBookings.value) return bookings.value
  return bookings.value.filter(b => b.status === 'booked' || b.status === 'completed')
})

const hiddenBookingsCount = computed(() => {
  if (!bookings.value || bookings.value.length === 0) return 0
  return bookings.value.filter(b => b.status !== 'booked' && b.status !== 'completed').length
})

const canEdit = computed(() => !props.readOnly && hasPermission('waivers_update'))

// Mirrors the delete lambda's gate: a waiver on a completed booking is kept.
const canDelete = computed(() => !props.readOnly && hasPermission('waivers_delete') &&
  !!selectedWaiver.value && selectedWaiver.value.booking_status !== 'completed')

// On the customer page the link would point at the page already open.
const showCustomerLink = computed(() => {
  const w = selectedWaiver.value
  if (!hasPermission('customer_view') || !w || !w.user_id) return false
  return props.currentCustomerId == null || Number(w.user_id) !== Number(props.currentCustomerId)
})

function getDate(d: any) {
  return formattedDate(dateFormat, d)
}

function getDisplayTime(slot: any) {
  // Walk-in waivers have no slot time. Show a dash rather than a formatted empty time.
  if (slot && !slot.start_time && Number(slot.is_all_day) !== 1) {
    return '-'
  }
  return displayTime(slot, timeFormat)
}

function getSignedDate(d: any) {
  return formatGroupDate(d, { dayOfWeek: false })
}

function getSignedTime(d: any) {
  return formatGroupTime(d)
}

function formatPhone(phone: string) {
  return formatPhoneDisplay(phone)
}

function getConsentLabel(name: string) {
  const field = Array.isArray(consentFields.value)
    ? consentFields.value.find(f => f.name === name)
    : null
  if (field && field.label) return field.label
  const isOptin = field ? !!field.is_optin : false
  if (name === 'Email Opt Out') {
    return isOptin ? 'Yes, sign me up for marketing emails' : 'Do not include me in mailing lists'
  }
  if (name === 'Photo Opt Out') {
    return isOptin ? 'I consent to my photos being shared on social media' : 'Do not share my photos on social media'
  }
  return ''
}

function hasConsentField(name: string) {
  return Array.isArray(consentFields.value) && consentFields.value.some(f => f.name === name)
}

function getConsentBadgeLabel(name: string) {
  const field = Array.isArray(consentFields.value)
    ? consentFields.value.find(f => f.name === name)
    : null
  const isOptIn = field ? !!field.is_optin : false
  const optoutValue = name === 'Email Opt Out'
    ? !!selectedWaiver.value.email_optout
    : !!selectedWaiver.value.picture_optout
  // The customer's literal checkbox state. Opt-in mode + box checked = consent.
  // Opt-out mode + box checked = refusal. XOR captures both.
  const ticked = isOptIn !== optoutValue
  const subject = name === 'Email Opt Out' ? 'Email' : 'Photo'
  const mode = isOptIn ? 'opt-in' : 'opt-out'
  return `${subject} ${mode}: ${ticked ? 'yes' : 'no'}`
}

function formatCustomFieldResponse(response: any) {
  if (response.type === 1) {
    return response.response === '1' || response.response === 1 ? 'Yes' : 'No'
  }
  return response.response || 'Not provided'
}

function copyCustomFields(format: string) {
  const fields = customFieldResponses.value
  if (!fields || !fields.length) return
  let text
  if (format === 'csv') {
    const header = 'Field,Response'
    const rows = fields.map(f => {
      const label = (f.label || '').replace(/"/g, '""')
      const value = (formatCustomFieldResponse(f) || '').replace(/"/g, '""')
      return `"${label}","${value}"`
    })
    text = [header, ...rows].join('\n')
  } else {
    text = fields.map(f => `${f.label}: ${formatCustomFieldResponse(f)}`).join('\n')
  }
  navigator.clipboard.writeText(text).then(() => {
    proxy.$notify({ type: 'success', message: 'Copied to clipboard' })
  }).catch(() => {
    proxy.$notify({ type: 'danger', message: 'Could not copy. Please try again.' })
  })
}

async function loadWaiverDetail(id: number) {
  drawerLoading.value = true
  try {
    // includeSignature pulls the signature (decoded data-URL) + waiver_text in the
    // same round trip, so the in-drawer signature renders and Download PDF has
    // everything it needs without a second fetch.
    const { waiver, custom_field_responses, consent_fields } =
      await fetchWaiverDetail(id, { includeSignature: true })
    // Merge: keep list-provided fields (game_instance_id, img) and overlay detail fields.
    // Preserve not_participating value the user may have toggled before fetch resolved.
    const userToggled = selectedWaiver.value && selectedWaiver.value.id === id
      ? selectedWaiver.value.not_participating
      : null
    selectedWaiver.value = { ...selectedWaiver.value, ...waiver }
    // Cast tinyint(1) to true boolean for el-switch v-model
    selectedWaiver.value.not_participating = userToggled !== null
      ? !!userToggled
      : !!selectedWaiver.value.not_participating
    customFieldResponses.value = custom_field_responses || []
    consentFields.value = consent_fields || []
  } catch (err: any) {
    console.log(err)
    proxy.$notify({
      type: 'danger',
      message: err && err.response && err.response.status === 401
        ? 'You do not have access to this action.'
        : 'Could not load waiver details. Please try again.',
    })
  } finally {
    drawerLoading.value = false
  }
}

async function downloadWaiverPdf() {
  if (!selectedWaiver.value || pdfGenerating.value) return
  // selectedWaiver already carries the decoded signature + waiver_text from
  // loadWaiverDetail's include=signature fetch, so the PDF is fully populated.
  pdfGenerating.value = true
  try {
    await waiverPdf.value.generate({
      waiver: selectedWaiver.value,
      consent_fields: consentFields.value,
      custom_field_responses: customFieldResponses.value,
    })
  } catch (err) {
    console.error('Failed to generate waiver PDF', err)
  } finally {
    pdfGenerating.value = false
  }
}

async function toggleNotParticipating() {
  // Auto-save the not_participating toggle. Reverts the switch on failure.
  if (!selectedWaiver.value || !selectedWaiver.value.id) return
  const newValue = selectedWaiver.value.not_participating
  try {
    const config = await authConfig()
    const data = {
      booking_slot_id: selectedWaiver.value.booking_slot_id,
      not_participating: newValue,
    }
    await axios.put(waiverUrl(), data, config)
    proxy.$notify({
      type: 'success',
      message: newValue ? 'Marked as not participating' : 'Marked as participating',
    })
    emit('changed', { action: 'participation', waiverId: selectedWaiver.value.id, notParticipating: !!newValue })
  } catch (err) {
    console.log(err)
    // Revert the toggle on error
    selectedWaiver.value.not_participating = !newValue
    proxy.$notify({
      type: 'danger',
      message: 'Could not update. Please try again.',
    })
  }
}

function viewCustomerFromDrawer() {
  const waiver = selectedWaiver.value
  if (!waiver || !waiver.user_id) return
  const route = proxy.$router.resolve({ path: '/customers/details', query: { id: waiver.user_id } })
  window.open(route.href, '_blank')
}

function viewEventFromDrawer() {
  const waiver = selectedWaiver.value
  if (!waiver || !waiver.game_instance_id) return
  const route = proxy.$router.resolve({ path: '/games/history/instance', query: { id: waiver.game_instance_id } })
  window.open(route.href, '_blank')
}

function moveWaiverFromDrawer() {
  isCopyMode.value = false
  selectedBooking.value = ''
  drawerShowAllBookings.value = false
  current.value = pickerStartDate()
  drawerView.value = 'booking-selector'
  changeDate()
}

function copyWaiverFromDrawer() {
  isCopyMode.value = true
  selectedBooking.value = ''
  drawerShowAllBookings.value = false
  current.value = pickerStartDate()
  drawerView.value = 'booking-selector'
  changeDate()
}

// The picker opens on the booking's day, or for a waiver with no booking, the day it was signed.
function pickerStartDate() {
  const w = selectedWaiver.value
  if (w && w.booking_date) return String(w.booking_date).slice(0, 10)
  if (w && w.created_time && moment(w.created_time).isValid()) return moment(w.created_time).format('YYYY-MM-DD')
  return moment().format('YYYY-MM-DD')
}

function backToDetail() {
  drawerView.value = 'detail'
  isCopyMode.value = false
  drawerShowAllBookings.value = false
  selectedBooking.value = selectedWaiver.value && selectedWaiver.value.booking_slot_id
    ? { id: selectedWaiver.value.booking_slot_id }
    : ''
}

function closeDrawer() {
  drawerView.value = 'detail'
  isCopyMode.value = false
  drawerShowAllBookings.value = false
  selectedBooking.value = ''
  clearPrompt.show = false
  deletePrompt.show = false
  emit('close')
}

// Maps a waiver PUT/POST/DELETE failure to plain copy. The lambdas return plain strings.
function waiverActionError(err: any, action?: 'delete') {
  const status = err && err.response && err.response.status
  const body = err && err.response && err.response.data
  if (status === 401) return 'You do not have access to this action.'
  if (body === 'Not enabled') return 'Waivers are not enabled on your subscription plan.'
  if (body === 'Invalid booking slot') return 'That booking is not available. Pick another one.'
  if (body === 'Waiver is part of a completed game') {
    return action === 'delete'
      ? 'This waiver is part of a completed event and cannot be deleted.'
      : 'This waiver is part of a completed event and cannot be cleared.'
  }
  if (body === 'Waiver has no template') return 'This waiver can only live on a booking. Use Move to change its booking.'
  return 'Something went wrong. Please try again.'
}

async function clearWaiverBooking() {
  if (clearPrompt.loading || !selectedWaiver.value || !selectedWaiver.value.id) return
  const id = selectedWaiver.value.id
  clearPrompt.loading = true
  try {
    const config = await authConfig()
    await axios.put(waiverUrl(), { clear_booking: true }, config)
    clearPrompt.show = false
    // Let the dialog hand the scroll lock back while the drawer is still open (see deleteWaiver).
    await nextTick()
    proxy.$notify({ type: 'success', message: 'Booking cleared. The waiver is a walk-in.' })
    emit('changed', { action: 'cleared', waiverId: id })
    closeDrawer()
  } catch (err) {
    console.log(err)
    clearPrompt.show = false
    proxy.$notify({ type: 'danger', message: waiverActionError(err) })
  } finally {
    clearPrompt.loading = false
  }
}

async function updateWaiver() {
  if (saving.value || !selectedBooking.value || !selectedWaiver.value) return
  const wasWalkin = isWalkinWaiver.value
  const copying = isCopyMode.value
  const id = selectedWaiver.value.id
  saving.value = true
  try {
    const config = await authConfig()
    if (copying) {
      // Copy creates a second waiver on the chosen booking.
      await axios.post(waiverUrl(), { booking_slot_id: selectedBooking.value.id }, config)
    } else {
      // Move or assign updates this waiver in place.
      await axios.put(waiverUrl(), {
        booking_slot_id: selectedBooking.value.id,
        not_participating: selectedWaiver.value.not_participating,
      }, config)
    }
    proxy.$notify({
      type: 'success',
      message: copying ? 'Waiver copied to the booking' : (wasWalkin ? 'Waiver assigned to the booking' : 'Waiver moved to the booking'),
    })
    emit('changed', { action: copying ? 'copied' : (wasWalkin ? 'assigned' : 'moved'), waiverId: id })
    closeDrawer()
  } catch (err) {
    console.log(err)
    proxy.$notify({ type: 'danger', message: waiverActionError(err) })
  } finally {
    saving.value = false
  }
}

async function deleteWaiver() {
  if (deletePrompt.loading || !selectedWaiver.value || !selectedWaiver.value.id) return
  const id = selectedWaiver.value.id
  deletePrompt.loading = true
  try {
    const config = await authConfig()
    await axios.delete(waiverUrl(), config)
    deletePrompt.show = false
    // Let the dialog hand the scroll lock back while the drawer is still open: the dialog
    // restores the overflow it saved on open ('hidden', set by the drawer). If both close in
    // the same tick, BaseDrawer clears the lock first and the dialog then re-locks the page.
    await nextTick()
    proxy.$notify({ type: 'success', message: 'Waiver deleted' })
    emit('deleted', id)
    closeDrawer()
  } catch (err) {
    console.log(err)
    deletePrompt.show = false
    proxy.$notify({ type: 'danger', message: waiverActionError(err, 'delete') })
  } finally {
    deletePrompt.loading = false
  }
}

async function getBookings() {
  bookingsLoading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(groupUrl() + '/bookings?day=' + current.value + '&type=2', config)
    bookings.value = response.data.bookings || []
    emptyBooking.value = bookings.value.length <= 0
  } catch (err: any) {
    console.log(err)
    bookings.value = []
    proxy.$notify({
      type: 'danger',
      message: err && err.response && err.response.status === 401
        ? 'You do not have access to this action.'
        : 'Could not load bookings. Please try again.',
    })
  } finally {
    bookingsLoading.value = false
  }
}

function changeDate() {
  getBookings()
}

function onKeydown(e: KeyboardEvent) {
  // The alert dialogs handle their own ESC; never close the drawer underneath them.
  if (e.key !== 'Escape' || clearPrompt.show || deletePrompt.show) return
  closeDrawer()
}

watch(() => props.show, (open) => {
  if (open && props.waiver) {
    document.addEventListener('keydown', onKeydown)
    // Cast tinyint(1) to a true boolean for the el-switch v-model
    selectedWaiver.value = { ...props.waiver, not_participating: !!props.waiver.not_participating }
    selectedBooking.value = props.waiver.booking_slot_id ? { id: props.waiver.booking_slot_id } : ''
    customFieldResponses.value = []
    consentFields.value = []
    drawerView.value = 'detail'
    isCopyMode.value = false
    drawerShowAllBookings.value = false
    loadWaiverDetail(props.waiver.id)
  } else {
    document.removeEventListener('keydown', onKeydown)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
})

return { __sfc: true,props, emit, proxy, group, dateFormat, timeFormat, selectedWaiver, selectedBooking, bookings, emptyBooking, current, customFieldResponses, consentFields, pdfGenerating, drawerLoading, drawerView, isCopyMode, bookingsLoading, drawerShowAllBookings, saving, clearPrompt, deletePrompt, waiverPdf, hasPermission, authConfig, groupUrl, waiverUrl, isWalkinWaiver, canClearBooking, filteredBookings, hiddenBookingsCount, canEdit, canDelete, showCustomerLink, getDate, getDisplayTime, getSignedDate, getSignedTime, formatPhone, getConsentLabel, hasConsentField, getConsentBadgeLabel, formatCustomFieldResponse, copyCustomFields, loadWaiverDetail, downloadWaiverPdf, toggleNotParticipating, viewCustomerFromDrawer, viewEventFromDrawer, moveWaiverFromDrawer, copyWaiverFromDrawer, pickerStartDate, backToDetail, closeDrawer, waiverActionError, clearWaiverBooking, updateWaiver, deleteWaiver, getBookings, changeDate, onKeydown, Badge, isValidDate }
}

})